import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { type PropertyDocumentKind, type PropertyFileResponse } from '@/shared/api';
import { partitionLocked } from './akteBulkRules';
import { requiresIssuedOn } from './akteKindLabels';
import { ReclassifyFields } from './ReclassifyFields';
import type { IssuedOnResolver } from './useAkteBulkActions';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: PropertyFileResponse[];
  /** Vorbelegtes Ziel-Kind beim Cross-Category-Drop; ohne = Kind der ersten Datei. */
  suggestedKind?: PropertyDocumentKind | null;
  onReclassify: (kind: PropertyDocumentKind, issuedOn: IssuedOnResolver) => Promise<void>;
}

/** Bulk-„Kategorie ändern": ein Ziel-Kind für die ganze Auswahl, gemeinsames Datum wo nötig (§4.7). */
export function BulkReclassifyDialog({
  open,
  onOpenChange,
  files,
  suggestedKind,
  onReclassify,
}: Props) {
  const [kind, setKind] = useState<PropertyDocumentKind>(
    suggestedKind ?? files[0]?.kind ?? 'Sonstiges',
  );
  const [issuedOn, setIssuedOn] = useState('');
  const [dateError, setDateError] = useState(false);
  const [busy, setBusy] = useState(false);

  // Nur nicht gesperrte Belege werden tatsächlich umklassifiziert (gesperrte überspringt der Server).
  const { mutable, locked } = partitionLocked(files);
  // Gemeinsames Datum nur nötig, wenn das Ziel-Kind eins verlangt UND mindestens eine der
  // tatsächlich zu ändernden Dateien keins hat.
  const needsDate = requiresIssuedOn(kind) && mutable.some((f) => !f.issuedOn);

  async function submit() {
    if (needsDate && !issuedOn) {
      setDateError(true);
      return;
    }
    setBusy(true);
    try {
      await onReclassify(kind, (f) => f.issuedOn ?? (needsDate ? issuedOn : null));
      onOpenChange(false);
    } catch {
      // Fehler wird als Toast gezeigt (Mutation); Dialog bleibt offen zur Korrektur.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Kategorie ändern — {mutable.length} {mutable.length === 1 ? 'Datei' : 'Dateien'}
          </DialogTitle>
          <DialogDescription>
            Ein neues Ziel für die ganze Auswahl. Achtung: das ändert die Bedeutung der Belege
            (Finanz-Zuordnung, Sichtbarkeit).
            {locked.length > 0 && ` ${locked.length} gesperrte Belege werden übersprungen.`}
          </DialogDescription>
        </DialogHeader>
        <ReclassifyFields
          idPrefix="bulk-reclassify"
          kind={kind}
          onKindChange={setKind}
          needsDate={needsDate}
          issuedOn={issuedOn}
          onIssuedOnChange={(v) => {
            setIssuedOn(v);
            setDateError(false);
          }}
          dateError={dateError}
          dateHint="für Dateien ohne eigenes Datum"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant="brand" loading={busy} onClick={submit}>
            Umklassifizieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
